import { useUserData } from "../contexts/UserContext";
import { useState, useEffect, useRef } from "react";
import { PersonRecord } from "../types/data";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import CustomSelect from "../components/CustomSelect";
import AddressPicker, {
  AddressPickerHandle,
} from "../components/AddressPicker";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import { convertInCurrentLang } from "../utils/misc";
import { motion } from "framer-motion";
import { authService } from "../services/auth.service";
import { storageService } from "../services/storage.service";
import { AppSettings } from "../types/data";
import { convertNumberInCurrentLang } from "../utils/misc";

const EditProfile = () => {
  /* get ID parameter from URL */
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const { getUser, getPerson, updatePerson } = useUserData();
  const user = getUser();
  const currencySymbol = user?.channel_details?.currency_symbol || '৳';
  const [profile, setProfile] = useState<PersonRecord | null>(null);
  const [error, setError] = useState("");
  const [isUser, setIsUser] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const addressPickerRef = useRef<AddressPickerHandle>(null);

  useEffect(() => {
    enableStickyWatcher();
    
    // Load settings
    storageService.getSettings().then(setSettings).catch(console.error);
  }, []);

  useEffect(() => {
    if (!id) return;

    const targetPerson = getPerson(id) || null;

    if (targetPerson) {
      // console.log("EditProfile:: targetPerson", targetPerson);
      const userUuid = (user as any)?.uuid ?? user?.id;
      const is_user = !!userUuid && userUuid === targetPerson.id;
      setIsUser(is_user);
      if (is_user && user) {
        targetPerson.surveyor_id = (user as any)?.uuid ?? user.id;
      }
      setProfile(targetPerson);
    } else {
      const userUuid = (user as any)?.uuid ?? user?.id;
      if (user && userUuid && id === userUuid) {
      const now = new Date().toISOString();
      const newUserPerson: PersonRecord = {
        version: 1,
        id: userUuid,
        name: user.name,
        surveyor_id: userUuid,
        mobile: "",
        age: undefined,
        sex: undefined,
        occupation: undefined,
        address: {
          district: "",
          upazila: "",
          union: "",
          village: "",
        },
        notes: "",
        records: [],
        added_on: now,
        updated_at: now,
      };
      setProfile(newUserPerson);
      } else {
        setError(t("profileNotFound"));
      }
    }
  }, [id]);

  const handleChange = (field: keyof PersonRecord, value: any) => {
    setProfile((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleNumberChange = (val: string) => {
    const converted = convertInCurrentLang(lang, val);
  };

  const handleSubmit = () => {
    if (!profile) return;

    if (!profile.name?.trim()) {
      setError(t("requiredFields"));
      window.scrollTo(0, 0);
      return null;
    }

    // Check age only if the field is visible
    if ((settings?.surveyShowAge ?? true) && !profile.age) {
      setError(t("requiredFields"));
      window.scrollTo(0, 0);
      return null;
    }

    const minAge = settings?.surveyMinAge ?? 0;
    const maxAge = settings?.surveyMaxAge ?? 120;
    const is_age_valid = Number.isInteger(profile.age) && Number(profile.age) >= minAge && Number(profile.age) <= maxAge;
    if ((settings?.surveyShowAge ?? true) && !is_age_valid) {
      setError(t("invalidAge").replace("{min}", convertNumberInCurrentLang(lang, minAge)).replace("{max}", convertNumberInCurrentLang(lang, maxAge)));
      window.scrollTo(0, 0);
      return null;
    }

    //Wait for address picker to commit
    if ((settings?.surveyShowAddress ?? false) && (settings?.surveyAddressType ?? 'bangladesh') === 'bangladesh') {
      addressPickerRef.current?.commit();
      let new_address = addressPickerRef.current?.address || {};
      // Make sure address is properly updated on mobile, as onBlur event might not trigger
      let updatedProfile = { ...profile, address: new_address };
      updatePerson({ ...updatedProfile, updated_at: new Date().toISOString() });
    } else {
      updatePerson({ ...profile, updated_at: new Date().toISOString() });
    }
    
    // If editing user's own profile, sync name to server and local auth
    if (isUser && user && profile) {
      authService.updateUserProfile({ name: profile?.name || "" })
        .then(() => {
          console.log('User profile synced to server');
        })
        .catch((error) => {
          console.error('Failed to sync user profile to server:', error);
          // Fallback: at least update locally
          authService.updateLocalUser({ name: profile?.name || "" }).catch(console.error);
        });
    }
    
    alert(t("profileUpdated"));
  };

  return (
    <div
      className={
        "page relative flex flex-col grow pt-12 bg-transparent text-center"
      }
    >
      <div
        className="sticky-header sticky z-10 mb-4 px-8 py-4"
        data-watch-sticky
        data-sticky-top="auto"
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
            if (isUser) {
              navigate("/user/");
            } else {
              navigate(-1);
            }
          }}
            className="p-1 rounded-full text-pink-600 bg-white/75 hover:bg-white transition-colors"
            aria-label={t("goBack")}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="size-6" viewBox="0 0 24 24"><path fill="currentColor" d="M20 11.25a.75.75 0 0 1 0 1.5h-9.25V18a.75.75 0 0 1-1.28.53l-6-6a.75.75 0 0 1 0-1.06l6-6a.75.75 0 0 1 1.28.53v5.25z" /></svg>
          </button>
          <h1><span className="text-pink-600">{t("editProfile")}</span></h1>
          <div className="w-8"></div>
        </div>        
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="space-y-4 mb-20 mt-8 px-4 max-w-lg mx-auto w-full"
      >
        {error && (
          <motion.div
            className="flex items-center justify-center text-center -mt-4 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <p className="bg-rose-700 text-white text-base rounded-full px-4 py-1">
              {error}
            </p>
          </motion.div>
        )}

        <div>
          <label className="block label">
            {(settings?.surveyNameFieldLabel ?? 'fullNameOrId') === 'idOnly' ? t("id") || "ID" : t("name")}
          </label>
          <input
            type="text"
            className="w-full input-base required"
            value={profile?.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            autoComplete="name"
          />
        </div>

        {(settings?.surveyShowMobile ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("mobile")}</label>
            <input
              type="text"
              className="w-full input-base"
              value={profile?.mobile || ""}
              onChange={(e) => handleChange("mobile", e.target.value)}
            />
          </div>
        )}

        {(settings?.surveyShowAge ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("age")}</label>
            <input
              type="number"
              min={settings?.surveyMinAge ?? 0}
              max={settings?.surveyMaxAge ?? 120}
              className="w-full input-base required"
              value={profile?.age || ""}
              onChange={(e) => handleChange("age", Number(e.target.value))}
              autoComplete="off"
              inputMode="numeric"
            />
          </div>
        )}

        {(settings?.surveyShowSex ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("sex")}</label>
            <CustomSelect
              value={profile?.sex || ""}
              onChange={(value) => handleChange("sex", value)}
              options={[
                { label: t("male"), value: "male" },
                { label: t("female"), value: "female" },
                { label: t("other"), value: "other" },
              ]}
              placeholder={t("selectSex")}
            />
          </div>
        )}

        {(settings?.surveyShowOccupation ?? true) && (
          <div className="!mt-8">
            <label className="block label">{t("occupation")}</label>
            <CustomSelect
              value={profile?.occupation || ""}
              onChange={(value) => handleChange("occupation", value)}
              options={[
                { label: t("farmer"), value: "farmer" },
                { label: t("fisherman"), value: "fisherman" },
                { label: t("government_officer"), value: "government_officer" },
                { label: t("government_employee"), value: "government_employee" },
                { label: t("private_service"), value: "private_service" },
                { label: t("business"), value: "business" },
                { label: t("teacher"), value: "teacher" },
                { label: t("student"), value: "student" },
                { label: t("housewife"), value: "housewife" },
                { label: t("other"), value: "other" },
              ]}
              placeholder={t("selectOccupation")}
            />
          </div>
        )}

        {(settings?.surveyShowIncome ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("monthlyIncome")}</label>
            <input
              type="number"
              min="0"
              max="100000000"
              className="w-full input-base"
              value={profile?.income ?? ""}
              onChange={(e) => handleChange("income", e.target.value === "" ? undefined : Number(e.target.value))}
              autoComplete="off"
              inputMode="numeric"
              placeholder={`${currencySymbol} 0`}
            />
          </div>
        )}

        {(settings?.surveyShowAddress ?? false) && (
          <div className="!mt-8">
            <label className="block label">{t("address")}</label>
            {(settings?.surveyAddressType ?? 'bangladesh') === 'bangladesh' ? (
              <AddressPicker
                ref={addressPickerRef}
                address={profile?.address}
                onChange={(addr) => handleChange("address", addr)}
              />
            ) : (
              <input
                type="text"
                className="w-full input-base"
                value={profile?.full_address || ""}
                onChange={(e) => handleChange("full_address", e.target.value)}
                autoComplete="street-address"
                placeholder="Enter full address"
              />
            )}
          </div>
        )}

        <div className="!mt-8">
          <label className="block label">{t("notes")}</label>
          <textarea
            value={profile?.notes || ""}
            onChange={(e) => handleChange("notes", e.target.value)}
            className="w-full input-base"
          />
        </div>
      </form>

      <div className="bottom-nav-bar flex justify-center gap-2 w-full sticky bottom-0 px-4 py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
        {/* <button
          onClick={() => {
            if (isUser) {
              navigate("/user/");
            } else {
              navigate(-1);
            }
          }}
          className="button-small button-bg-grad-2"
        >
          <div className="flex flex-row gap-2 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="3"
              stroke="currentColor"
              className="size-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            <span className="text-lg">{t("back")}</span>
          </div>
        </button> */}
        
        <button
          tabIndex={0}
          onClick={() => handleSubmit()}
          className="button-small button-bg-grad-1"
        >
          <div className="flex flex-row gap-2 items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
              />
            </svg>
            <span className="text-lg">{t("updateProfile")}</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default EditProfile;
